import React, { useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import ButtonBase from '@material-ui/core/ButtonBase';
import ClassNames from 'classnames';
import VideoPlayer from './VideoPlayer';
import PageContext from '../../PageContext';

const useStyles = makeStyles(theme => ({
  root: {
    alignItems: 'center',
    display: 'flex',
  },
  container: {
    [theme.breakpoints.up('1140')]: {
      width: 1140,
      padding: '24px 14px',
    },
    width: '100%',
    padding: '24px 5%',
  },
  introText: {
    color: props => props.introColor || '#fff',
    lineHeight: 1.3,
    fontSize: 37,
    [theme.breakpoints.down('xs')]: {
      fontSize: 27,
    },
    [theme.breakpoints.down('414')]: {
      fontSize: '1.5rem',
      marginBottom: 0,
    },
  },
  body: {
    ...theme.typography.h4,
    fontFamily: 'MadaRegular',
    color: props => props.introColor || '#fff',
    fontWeight: 400,
    [theme.breakpoints.down('xs')]: {
      ...theme.typography.body1,
    },
  },
  media: {
    width: '100%',
    // paddingTop: '56.25%',
    height: 0,
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      padding: '0 !important',
      marginBottom: 10,
    },
    '& img, video': {
      width: '100%',
      // position: 'absolute',
      top: 0,
      left: 0,
      [theme.breakpoints.down('xs')]: {
        padding: 0,
      },
      [theme.breakpoints.up('600')]: {
        maxHeight: 290,
      },
    },
  },
  intro: {
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    [theme.breakpoints.down('xs')]: {
      padding: '0 !important',
    },
    '& *': {
      color: props => props.introColor || '#fff',
    },
    '& p': {
      ...theme.typography.body2,
      margin: 0,
      color: props => props.introColor || '#fff',
      minHeight: 0,
    },
    '& ul': {
      listStyleType: 'none',
      padding: 0,
      margin: 0,
    },
    '& a': {
      color: props => props.introColor || '#fff',
    },
  },
  introInfo: {
    [theme.breakpoints.down('sm')]: {
      minHeight: 210,
    },
    [theme.breakpoints.down('991')]: {
      minHeight: 190,
    },
  },
  mt26: {
    marginTop: 10,
    [theme.breakpoints.down('xs')]: {
      marginTop: 15,
    },
  },
  infoWrapper: {
    margin: 0,
    maxWidth: '100%',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  videoWrapper: {
    alignItems: 'center',
  },
  videoMedia: {
    height: '100%',
    padding: '0 !important',
  },
  btn: {
    width: 'auto',
    height: 50,
    borderRadius: 2,
    padding: '0 20px',
    marginTop: 20,
    color: props =>
      `${props.btnTextColor} !important` || 'rgba(0, 0, 0, 0.87) !important',
    background: props => `${props.btnBGColor} !important`,
    ...theme.typography.button,
  },
  imageBg: {
    background: props => `url(${props.imageSrc}) no-repeat center / cover`,
    height: 270,
    maxHeight: 280,
    '& img': {
      maxHeight: '100%',
    },
  },
  introTitle: {
    fontSize: 27,
    [theme.breakpoints.down('xs')]: {
      fontSize: 20,
    },
  },
  grayscale: {
    filter: 'grayscale(1)',
  },
}));

function Intro() {
  const { clientInfo, landingInfo, textColor } = useContext(PageContext);
  const {
    pageText,
    introMedia,
    introTitle,
    intro,
    introVideo,
    primaryColor,
    fontColor,
  } = clientInfo[0];
  const hasPageText = !_isEmpty(pageText);
  const introColor = _get(pageText, 'hero.introColor') || fontColor;
  const buttonBGColor = _get(pageText, 'button.backgroundColor');
  const buttonTextColor = _get(pageText, 'button.textColor');
  const isMediaImage = !introVideo;
  const imageSrc = _get(introMedia, 'url');
  const classes = useStyles({
    btnTextColor: buttonTextColor
      ? `#${buttonTextColor}`
      : primaryColor
      ? `#${primaryColor}`
      : '',
    btnBGColor: buttonBGColor ? `#${buttonBGColor}` : '#FFF',
    introColor: introColor ? `#${introColor}` : textColor,
    imageSrc: isMediaImage ? imageSrc : '',
  });
  const hasMedia = introVideo || imageSrc;
  const pageTextInfo = () => {
    if (!hasPageText) return null;
    const {
      hero: { body, header },
    } = pageText;
    return (
      <>
        <Grid item xs={12}>
          <Typography variant="h1" className={classes.introText}>
            {header}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h4" className={classes.body}>
            {body}
          </Typography>
        </Grid>
      </>
    );
  };

  const shouldApplyGrayscaleFilter = _get(
    landingInfo,
    'pageText.grayscale',
    false,
  );
  return (
    <Grid item xs={12} sm={12} md={7} className={classes.root}>
      <Grid container>
        {pageTextInfo()}
        <Grid
          item
          xs={12}
          className={ClassNames(
            classes.introInfo,
            hasPageText ? classes.mt26 : null,
          )}
        >
          <Grid
            container
            direction="row"
            alignItems="flex-start"
            spacing={hasMedia ? 3 : 0}
            className={ClassNames(classes.infoWrapper, {
              [classes.videoWrapper]: introVideo,
            })}
          >
            {hasMedia &&
              (isMediaImage ? (
                <Grid
                  item
                  xs={12}
                  sm={5}
                  className={ClassNames(classes.media, {
                    [classes.imageBg]: classes.imageBg,
                    [classes.grayscale]: shouldApplyGrayscaleFilter,
                  })}
                >
                  <Hidden smUp>
                    <img src={imageSrc} alt="" />
                  </Hidden>
                </Grid>
              ) : (
                <Grid
                  item
                  xs={12}
                  sm={5}
                  className={ClassNames(classes.media, classes.videoMedia)}
                >
                  <VideoPlayer url={introVideo.url} thumbnail={imageSrc} />
                </Grid>
              ))}

            <Grid item xs={12} sm={hasMedia ? 7 : 12} className={classes.intro}>
              {introTitle && (
                <Typography variant="h4" className={classes.introTitle}>
                  {introTitle}
                </Typography>
              )}

              <RichTextReactRenderer
                data={intro.json}
                type="title"
                isLandingPage
                withHeroLinksStyles
              />
              {hasPageText && !_isEmpty(_get(pageText, 'button.label')) && (
                <ButtonBase href={pageText.button.url} className={classes.btn}>
                  {pageText.button.label}
                </ButtonBase>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

Intro.propTypes = {};

export default Intro;
